import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  RelanceCanal,
  RelanceNiveau,
  RelanceRow,
  RelanceStatut,
  RelanceTemplateRow,
} from '@/lib/database.types';

export function useRelances(filter: {
  entrepriseId?: string | null;
  echeanceId?: string | null;
  recouvrementId?: string | null;
  tierId?: string | null;
  niveau?: RelanceNiveau | null;
  canal?: RelanceCanal | null;
  statut?: RelanceStatut | null;
}) {
  return useQuery({
    queryKey: ['relances', filter],
    queryFn: async (): Promise<RelanceRow[]> => {
      let q = supabase.from('relances').select('*').order('created_at', { ascending: false });
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.echeanceId) q = q.eq('echeance_id', filter.echeanceId);
      if (filter.recouvrementId) q = q.eq('recouvrement_id', filter.recouvrementId);
      if (filter.tierId) q = q.eq('tier_id', filter.tierId);
      if (filter.niveau) q = q.eq('niveau', filter.niveau);
      if (filter.canal) q = q.eq('canal', filter.canal);
      if (filter.statut) q = q.eq('statut', filter.statut);
      q = q.limit(500);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export function useRelanceTemplates(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['relance_templates', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<RelanceTemplateRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('relance_templates')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('niveau', { ascending: true })
        .order('canal', { ascending: true });
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertTemplateInput {
  id?: string;
  entreprise_id: string;
  niveau: RelanceNiveau;
  canal: RelanceCanal;
  sujet?: string | null;
  contenu: string;
  is_default?: boolean;
}

export function useUpsertRelanceTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertTemplateInput): Promise<RelanceTemplateRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('relance_templates')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase
        .from('relance_templates')
        .insert(rest)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['relance_templates'] });
    },
  });
}

// Sprint 6.9.13 — XOR : exactement un de echeance_id ou recouvrement_id doit
// être fourni (contrainte CHECK relances_target_xor en DB).
export interface CreateRelanceInput {
  entreprise_id: string;
  echeance_id?: string | null;
  recouvrement_id?: string | null;
  tier_id: string;
  niveau: RelanceNiveau;
  canal: RelanceCanal;
  sujet?: string | null;
  contenu?: string | null;
  destinataire?: string | null;
  statut?: RelanceStatut;
  sent_at?: string | null;
  external_ref?: string | null;
}

export function useCreateRelance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRelanceInput): Promise<RelanceRow> => {
      const { data, error } = await supabase.from('relances').insert(input).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['relances'] });
    },
  });
}

export function useBulkCreateRelances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      inputs: CreateRelanceInput[],
    ): Promise<{ success: number; errors: string[] }> => {
      if (inputs.length === 0) return { success: 0, errors: [] };
      const { data, error } = await supabase.from('relances').insert(inputs).select('id');
      if (error) return { success: 0, errors: [error.message] };
      return { success: (data ?? []).length, errors: [] };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['relances'] });
    },
  });
}

export function renderTemplate(
  template: string,
  vars: Record<string, string | number | null | undefined>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const v = vars[key];
    return v == null ? '' : String(v);
  });
}
